// apps/client/src/hooks/useConversations.ts
//
// Firestore subscription to `companies/{c}/conversations`, ordered by
// `lastMessageAt desc`, filtered client-side by tab.
//
// Backend pipeline contract (canonical plan §1.4–§1.5, Worktree B):
//   - conversationId = `meta:${pageId}:${psid}`
//   - All writes server-side (`metaMessengerWebhook` + `sendOutboundMessage`).
//     Firestore rules `allow write: if false` on both `conversations` and
//     `messages` subcollection. We never mutate from the client; the
//     callable `sendOutboundMessage` is the only write path.
//   - `lastInboundCustomerMessageAt` is written on every inbound message
//     (canonical plan N1). Composer 24h gate reads it; null means no inbound
//     yet → send rejected.
//
// Tab filter mirrors leads page convention (LeadsPage.tsx STATUS_TABS):
//   'assigned_to_me' | 'unassigned' | 'closed' | 'all'.
//
// Broker-family scoping is applied to the QUERY, not just the snapshot.
// Firestore rules are a gate, never a filter: rule
// `isBrokerFamily() && resource.data.assignedBrokerId == request.auth.uid`
// (firestore.rules, match /conversations) rejects an unconstrained list
// query outright rather than trimming it, so the collection-wide
// `orderBy('lastMessageAt')` this hook used to issue failed with
// permission-denied for every broker and broker_assistant. The query must
// itself constrain to what the rule allows. Composite index
// `assignedBrokerId ASC, lastMessageAt DESC` already ships in
// firestore.indexes.json.
import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  limit as limitTo,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Roles the Firestore rule treats as broker-family for conversations, i.e.
 * readable only where `assignedBrokerId == request.auth.uid`. Mirrors
 * `isBrokerFamily()` in firestore.rules. credit_analyst is included because
 * the rule includes it, even though the DM inbox route capability
 * (`access_conversations_inbox`) excludes that role today: if the capability
 * is ever widened the query must already be correct.
 */
const BROKER_FAMILY_ROLES = new Set([
  'broker',
  'broker_assistant',
  'credit_analyst',
]);

export function isConversationBrokerFamily(role: string | undefined): boolean {
  return BROKER_FAMILY_ROLES.has(role ?? '');
}

/** Hard ceiling on the live listener. The inbox is a working queue, not an archive. */
export const CONVERSATIONS_PAGE_LIMIT = 200;

export type ConversationTab =
  | 'assigned_to_me'
  | 'unassigned'
  | 'closed'
  | 'all';

export interface ConversationParticipantProfile {
  firstName: string | null;
  lastName: string | null;
  profilePicUrl: string | null;
  hydratedAt: Timestamp | null;
}

export interface ConversationDoc {
  /** Firestore doc id; canonical `meta:${pageId}:${psid}`. */
  id: string;
  source: string;
  pageId: string;
  pageName?: string;
  psid: string;
  participantProfile: ConversationParticipantProfile;
  createdAt: Timestamp | null;
  lastMessageAt: Timestamp | null;
  lastMessageDirection: 'inbound' | 'outbound' | null;
  /** First 80 chars of last message text. Broker-only visibility per APP 13. */
  lastMessagePreview: string | null;
  unreadCount: number;
  assignedBrokerId: string | null;
  /** Inbound clock — drives 24h composer gate (N1). */
  lastInboundCustomerMessageAt: Timestamp | null;
  /** = lastInboundCustomerMessageAt + 24h. */
  windowExpiresAt: Timestamp | null;
  status: 'open' | 'closed' | 'archived';
  linkedLeadId?: string | null;
  capturedAt: Timestamp | null;
}

interface UseConversationsResult {
  /** Conversations matching the requested tab. */
  conversations: ConversationDoc[];
  /** The whole readable snapshot, for tab counts. Never a second listener. */
  allConversations: ConversationDoc[];
  loading: boolean;
  error: string | null;
  /** True when the caller is scoped to their own assigned threads. */
  brokerScoped: boolean;
}

/**
 * Subscribes once to the conversations the caller is allowed to read.
 *
 * Scope is set on the QUERY: broker-family callers get
 * `where('assignedBrokerId','==',uid)` because the Firestore rule rejects
 * any broader list query from them. Admin-tier and marketing callers read
 * company-wide, which is what the rule permits.
 *
 * Tab filtering runs client-side over that one snapshot, so switching tabs
 * never re-subscribes and the caller gets tab counts for free via
 * `allConversations`.
 */
export function useConversations(
  companyId: string | undefined,
  tab: ConversationTab,
  currentUid: string | undefined,
  role: string | undefined
): UseConversationsResult {
  const [allConversations, setAllConversations] = useState<ConversationDoc[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);

  const brokerScoped = isConversationBrokerFamily(role);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      setAllConversations([]);
      return;
    }
    // A broker-family caller with no uid yet cannot form a legal query;
    // issuing the unscoped one would be rejected by the rule. Wait.
    if (brokerScoped && !currentUid) {
      setLoading(true);
      setAllConversations([]);
      return;
    }
    setLoading(true);
    setError(null);

    const base = collection(db, `companies/${companyId}/conversations`);
    const q = brokerScoped
      ? query(
          base,
          where('assignedBrokerId', '==', currentUid),
          orderBy('lastMessageAt', 'desc'),
          limitTo(CONVERSATIONS_PAGE_LIMIT)
        )
      : query(
          base,
          orderBy('lastMessageAt', 'desc'),
          limitTo(CONVERSATIONS_PAGE_LIMIT)
        );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs: ConversationDoc[] = [];
        snap.forEach((d) => {
          const data = d.data() as Partial<ConversationDoc>;
          docs.push({
            id: d.id,
            source: data.source ?? 'meta_messenger',
            pageId: data.pageId ?? '',
            pageName: data.pageName,
            psid: data.psid ?? '',
            participantProfile: data.participantProfile ?? {
              firstName: null,
              lastName: null,
              profilePicUrl: null,
              hydratedAt: null,
            },
            createdAt: data.createdAt ?? null,
            lastMessageAt: data.lastMessageAt ?? null,
            lastMessageDirection: data.lastMessageDirection ?? null,
            lastMessagePreview: data.lastMessagePreview ?? null,
            unreadCount:
              typeof data.unreadCount === 'number' ? data.unreadCount : 0,
            assignedBrokerId: data.assignedBrokerId ?? null,
            lastInboundCustomerMessageAt:
              data.lastInboundCustomerMessageAt ?? null,
            windowExpiresAt: data.windowExpiresAt ?? null,
            status: (data.status as ConversationDoc['status']) ?? 'open',
            linkedLeadId: data.linkedLeadId ?? null,
            capturedAt: data.capturedAt ?? null,
          });
        });
        setAllConversations(docs);
        setLoading(false);
      },
      (err) => {
        // Surface read failures explicitly per
        // [[feedback-silent-fail-catch]]; default Firestore behaviour
        // would silently log + leave loading=true forever.
        console.error('[useConversations] subscription failed', err);
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    );

    return () => unsub();
  }, [companyId, brokerScoped, currentUid]);

  const conversations = useMemo(() => {
    switch (tab) {
      case 'assigned_to_me':
        return currentUid
          ? allConversations.filter((c) => c.assignedBrokerId === currentUid)
          : [];
      case 'unassigned':
        return allConversations.filter(
          (c) => !c.assignedBrokerId && c.status !== 'closed'
        );
      case 'closed':
        return allConversations.filter((c) => c.status === 'closed');
      case 'all':
      default:
        return allConversations;
    }
  }, [allConversations, tab, currentUid]);

  return { conversations, allConversations, loading, error, brokerScoped };
}
